/**
 * Top-level panel for Settings → LLM. Composes ScopeToggle, LlmConfigList,
 * ProviderKeyPanel, AgentCredentialPanel, and TaskRoutingPanel into the
 * full screen, owning the working draft state and the save handler.
 */
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { getLlmBundle, updateLlmBundle } from '@/api/settings'
import type {
  LlmConfig,
  LlmScope,
  LlmSettingsBundle,
  ProviderKeys,
  AgentCredentials,
  ImageGenConfig,
  EmbeddingConfig,
} from '@/types'
import type { ProviderId, AgentId, TaskType } from '@/data/llmCatalog'
import { opencodeModelProvider } from '@/data/llmCatalog'
import Button from '@/components/common/Button'

import ScopeToggle from './ScopeToggle'
import LlmConfigList from './LlmConfigList'
import LlmConfigEditor from './LlmConfigEditor'
import ProviderKeyPanel from './ProviderKeyPanel'
import AgentCredentialPanel, { AGENT_CRED_FIELD } from './AgentCredentialPanel'
import TaskRoutingPanel from './TaskRoutingPanel'

export default function LlmSettingsPanel() {
  const [bundle, setBundle] = useState<LlmSettingsBundle | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Working draft: starts equal to the loaded bundle and tracks edits
  // before the user clicks Save. Credential fields default to empty
  // strings — "no change" — so a masked save round-trip doesn't wipe
  // stored keys.
  const [scope, setScope] = useState<LlmScope>('tenant')
  const [configs, setConfigs] = useState<LlmConfig[]>([])
  const [defaultName, setDefaultName] = useState('')
  const [overrides, setOverrides] = useState<Partial<Record<TaskType, string>>>({})
  const [providerDraft, setProviderDraft] = useState<ProviderKeys>({})
  const [agentDraft, setAgentDraft] = useState<AgentCredentials>({})
  const [imageConfig, setImageConfig] = useState<ImageGenConfig>({})
  const [embeddingConfig, setEmbeddingConfig] = useState<EmbeddingConfig>({})

  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<LlmConfig | null>(null)

  // --- Load ---------------------------------------------------------------

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const b = await getLlmBundle()
        if (cancelled) return
        applyBundle(b)
      } catch (e: any) {
        toast.error('Failed to load LLM settings: ' + (e?.message || 'unknown error'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  function applyBundle(b: LlmSettingsBundle) {
    setBundle(b)
    setScope(b.scope)
    setConfigs(b.configs || [])
    setDefaultName(b.default_config_name || '')
    setOverrides(b.task_overrides || {})
    // Image-gen profile: fall back to the inherited tenant profile when the
    // personal scope hasn't set one, so the form reflects what's effective.
    const ownImage = b.image_config
    const ownEmbedding = b.embedding_config
    const hasOwnImage = !!ownImage && !!(ownImage.config_name || ownImage.provider || ownImage.model)
    setImageConfig(hasOwnImage ? ownImage! : (b.tenant_bundle?.image_config || {}))
    setEmbeddingConfig(
      ownEmbedding && Object.keys(ownEmbedding).length
        ? ownEmbedding
        : (b.tenant_bundle?.embedding_config || {}),
    )
    // Reset the drafts to empty — masked round-trip semantics.
    setProviderDraft({})
    setAgentDraft({})
  }

  /** The OWN bundle view for a given scope: the active bundle itself when it
   *  matches, otherwise the sibling sub-bundle the backend always ships. */
  function scopeOwnView(
    b: LlmSettingsBundle | null,
    s: LlmScope,
  ): Omit<LlmSettingsBundle, 'tenant_bundle' | 'user_bundle' | 'scope'> | undefined {
    if (!b) return undefined
    if (s === b.scope) return b
    return s === 'tenant' ? b.tenant_bundle : b.user_bundle
  }

  // Switching scope must re-point the editable config list at THAT scope's own
  // configs — otherwise a delete after a switch would save to the new scope and
  // silently leave the original scope's config in place (orphaned, still
  // inherited by every other tenant user). Swaps from the sibling view the
  // backend ships; unsaved edits to the scope being left are discarded (a
  // deliberate context switch), and Save still commits to the selected scope.
  function handleScopeChange(next: LlmScope) {
    if (next === scope) return
    const view = scopeOwnView(bundle, next)
    setScope(next)
    setConfigs(view?.configs || [])
    setDefaultName(view?.default_config_name || '')
    setOverrides(view?.task_overrides || {})
    const ownImage = view?.image_config
    const ownEmbedding = view?.embedding_config
    const hasOwnImage = !!ownImage && !!(ownImage.config_name || ownImage.provider || ownImage.model)
    setImageConfig(hasOwnImage ? ownImage! : (bundle?.tenant_bundle?.image_config || {}))
    setEmbeddingConfig(
      ownEmbedding && Object.keys(ownEmbedding).length
        ? ownEmbedding
        : (bundle?.tenant_bundle?.embedding_config || {}),
    )
    setProviderDraft({})
    setAgentDraft({})
  }

  // --- Derived: which providers / agents are actually referenced ---------

  const usedProviders = useMemo<ProviderId[]>(() => {
    const set = new Set<ProviderId>()
    for (const c of configs) {
      if (c.mode === 'api') set.add(c.provider)
      else if (c.auth === 'api_key') {
        // Agent in api_key mode borrows the provider's key — for OpenCode,
        // the provider its model id names (`openai/gpt-5` → OpenAI).
        const map: Partial<Record<AgentId, ProviderId>> = {
          claude_code: 'anthropic',
          codex: 'openai',
        }
        const p = c.agent === 'opencode' ? opencodeModelProvider(c.model) : map[c.agent]
        if (p) set.add(p)
      }
    }
    // The image-gen profile reuses a provider key, so surface that
    // provider's key field even if no text config uses it.
    if (imageConfig.provider) set.add(imageConfig.provider)
    // Same for a DEDICATED embedding profile — it is the only way a tenant
    // running everything on agent configurations ever gets asked for the
    // OpenAI/Google key its vector index needs.
    if (embeddingConfig.source === 'custom' && embeddingConfig.provider) {
      set.add(embeddingConfig.provider)
    }
    return Array.from(set)
  }, [configs, imageConfig.provider, embeddingConfig.source, embeddingConfig.provider])

  const agentNeeds = useMemo(() => {
    const seen = new Set<string>()
    const out: { agent: AgentId; auth: 'api_key' | 'subscription' | 'configured' }[] = []
    for (const c of configs) {
      if (c.mode !== 'agent') continue
      const key = `${c.agent}:${c.auth}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push({ agent: c.agent, auth: c.auth })
    }
    return out
  }, [configs])

  // --- Config CRUD --------------------------------------------------------

  function openAdd() {
    setEditing(null)
    setEditorOpen(true)
  }

  function openEdit(c: LlmConfig) {
    setEditing(c)
    setEditorOpen(true)
  }

  function handleSaveConfig(next: LlmConfig) {
    setConfigs((prev) => {
      // Edit: replace by old name; Add: append.
      const oldName = editing?.name
      let updated: LlmConfig[]
      if (oldName) {
        updated = prev.map((c) => (c.name === oldName ? next : c))
      } else {
        updated = [...prev, next]
      }
      return updated
    })
    // If the old default was renamed, point it at the new name; if there's
    // no default yet (first add), set the new one as default.
    setDefaultName((d) => {
      if (!d) return next.name
      if (editing && editing.name === d && next.name !== d) return next.name
      return d
    })
    // Same for overrides — repoint any task that referenced the old name.
    if (editing && editing.name !== next.name) {
      setOverrides((prev) => {
        const updated: Partial<Record<TaskType, string>> = {}
        for (const [task, name] of Object.entries(prev) as [TaskType, string][]) {
          updated[task] = name === editing!.name ? next.name : name
        }
        return updated
      })
    }
    setEditorOpen(false)
    setEditing(null)
  }

  function handleDeleteConfig(name: string) {
    if (!confirm(`Delete configuration "${name}"?`)) return
    setConfigs((prev) => prev.filter((c) => c.name !== name))
    if (defaultName === name) setDefaultName('')
    setOverrides((prev) => {
      const updated: Partial<Record<TaskType, string>> = {}
      for (const [task, n] of Object.entries(prev) as [TaskType, string][]) {
        if (n !== name) updated[task] = n
      }
      return updated
    })
  }

  // --- Save ---------------------------------------------------------------

  async function handleSave() {
    // A subscription-auth agent config is unusable without its credential —
    // block the save until the account is connected (OAuth flow) or a token/
    // JSON blob is pasted in the credentials panel below.
    // Check the credential on the SELECTED scope's view (the one we're about to
    // write to), not just whatever scope loaded from the server.
    const savedMasked = scopeOwnView(bundle, scope) ?? bundle
    const missingCred = agentNeeds
      .filter((n) => n.auth === 'subscription' && AGENT_CRED_FIELD[n.agent])
      .map((n) => ({ agent: n.agent, field: AGENT_CRED_FIELD[n.agent]! }))
      .filter(({ field }) => !savedMasked?.agent_credentials?.[field] && !(agentDraft[field] || '').trim())
    if (missingCred.length > 0) {
      const names = missingCred
        .map(({ agent }) =>
          agent === 'claude_code' ? 'Claude Code' : 'Codex',
        )
        .join(', ')
      toast.error(
        `${names}: a configuration uses subscription authentication but no credential is connected. ` +
          'Connect the account (or paste a token) in "Agent subscription credentials" before saving.',
      )
      return
    }
    setSaving(true)
    try {
      const b = await updateLlmBundle({
        scope,
        configs,
        default_config_name: defaultName,
        task_overrides: overrides,
        provider_keys: providerDraft,
        agent_credentials: agentDraft,
        image_config: imageConfig,
        embedding_config: embeddingConfig,
      })
      applyBundle(b)
      // The Ask-AI dock lives in the Sidebar and stays mounted across routes,
      // so it resolves its backend (claude_code / codex / llm) + model chip
      // ONCE on mount and then only on this event. Without the dispatch a
      // changed default config / task override is ignored until a full page
      // reload — even after creating a new application.
      window.dispatchEvent(new CustomEvent('twasta:settings-changed'))
      toast.success('LLM settings saved')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  // --- Render -------------------------------------------------------------

  if (loading) {
    return <div style={{ padding: 24, color: 'var(--color-text-secondary)' }}>Loading…</div>
  }

  if (!bundle) {
    return (
      <div style={{ padding: 24, color: 'var(--color-text-secondary)' }}>
        LLM settings unavailable. Try reloading.
      </div>
    )
  }

  // Masked credential flags for the CURRENTLY SELECTED scope (follows the
  // toggle, not just the scope loaded from the server).
  const activeMasked = scopeOwnView(bundle, scope) ?? bundle

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 880 }}>
      <Section
        title="Scope"
        subtitle="Choose whether the settings below apply to your whole tenant or only to you."
      >
        <ScopeToggle value={scope} onChange={handleScopeChange} disabled={saving} />
      </Section>

      <Section
        title="LLM configurations"
        subtitle="A configuration bundles a mode (API call or Agent), provider/agent, model and parameters. Reference them by name in task routing below."
      >
        {configs.length === 0 && (
          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-bg-elevated)',
              border: '1px solid var(--color-border)',
              fontSize: 14,
              color: 'var(--color-text-secondary)',
              lineHeight: 1.5,
            }}
          >
            You currently use the <strong>platform default</strong> AI configuration —
            including which model and thinking level each task runs on. That default is
            managed by your instance administrator and cannot be changed here. Add a
            configuration below to route tasks to your own models instead.
          </div>
        )}
        <LlmConfigList
          configs={configs}
          defaultName={defaultName}
          onAdd={openAdd}
          onEdit={openEdit}
          onDelete={handleDeleteConfig}
        />
      </Section>

      {usedProviders.length > 0 && (
        <Section
          title="Provider API keys"
          subtitle="Only providers used by your configurations are shown. Keys are encrypted at rest; leave a field blank to keep the stored value."
        >
          <ProviderKeyPanel
            providers={usedProviders}
            masked={activeMasked.provider_keys as any}
            draft={providerDraft}
            onChange={setProviderDraft}
          />
        </Section>
      )}

      {agentNeeds.some((n) => n.auth === 'subscription' && AGENT_CRED_FIELD[n.agent]) && (
        <Section
          title="Agent subscription credentials"
          subtitle="Required when an Agent configuration is set to authenticate via a subscription account."
        >
          <AgentCredentialPanel
            needs={agentNeeds}
            masked={activeMasked.agent_credentials as any}
            draft={agentDraft}
            onChange={setAgentDraft}
            scope={scope}
            onCredentialStored={(field, isSet) =>
              // The OAuth connect/disconnect flow stores server-side directly
              // for the SELECTED scope; patch the masked flag on that scope's
              // view in place instead of reloading (a reload would clobber
              // unsaved edits elsewhere in the form).
              setBundle((prev) => {
                if (!prev) return prev
                const patch = (
                  v?: { agent_credentials: AgentCredentials },
                ) => (v ? { ...v, agent_credentials: { ...v.agent_credentials, [field]: isSet } } : v)
                if (scope === prev.scope) return patch(prev) as LlmSettingsBundle
                if (scope === 'tenant') return { ...prev, tenant_bundle: patch(prev.tenant_bundle) as any }
                return { ...prev, user_bundle: patch(prev.user_bundle) as any }
              })
            }
          />
        </Section>
      )}

      <Section
        title="Image generation"
        subtitle="Pick one of your LLM configurations above to power the page designer's “Generate with AI”. Only OpenAI and Google configs can generate images (Anthropic / DeepSeek have no image API). The provider, model and API key are reused from that configuration."
      >
        <ImageGenSection value={imageConfig} configs={configs} onChange={setImageConfig} />
      </Section>

      <Section
        title="Vector search (master records)"
        subtitle="Turns master records — employees, customers, items, branches — into searchable vectors, so a name a person says (“Bob in the Mumbai sales office”) resolves to the code the data stores. Reuse one of the API-mode configurations above, or configure a dedicated embedding provider — which is what you need when your configurations are all Agent mode, since an agent authenticates a CLI against a subscription and has no provider key to lend. Only OpenAI and Google publish an embeddings endpoint. Each project then selects this profile in its own settings before anything is indexed."
      >
        <EmbeddingSection
          value={embeddingConfig}
          configs={configs}
          providerKeySet={{
            openai: !!activeMasked.provider_keys?.openai_api_key
              || !!(providerDraft.openai_api_key || '').trim(),
            google: !!activeMasked.provider_keys?.google_api_key
              || !!(providerDraft.google_api_key || '').trim(),
          }}
          onChange={setEmbeddingConfig}
        />
      </Section>

      <Section
        title="Task routing"
        subtitle="Pick one configuration as the default. Optionally override individual tasks (FRS, page generation, project modify, …) with a different configuration."
      >
        <TaskRoutingPanel
          configs={configs}
          defaultName={defaultName}
          overrides={overrides}
          onDefaultChange={setDefaultName}
          onOverrideChange={(task, name) =>
            setOverrides((prev) => ({ ...prev, [task]: name }))
          }
        />
      </Section>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="primary" onClick={handleSave} loading={saving}>
          Save changes
        </Button>
      </div>

      <LlmConfigEditor
        open={editorOpen}
        scope={scope}
        initial={editing}
        existingNames={configs.map((c) => c.name)}
        onClose={() => {
          setEditorOpen(false)
          setEditing(null)
        }}
        onSave={handleSaveConfig}
      />
    </div>
  )
}

// Providers that actually expose an image-generation API. A named config is
// only eligible for image generation when it's an API-mode config on one of
// these providers.
const IMAGE_CAPABLE_PROVIDERS: ReadonlyArray<string> = ['openai', 'google']

// Providers with a text-embeddings API. Same two as images, for a different
// reason: Anthropic and DeepSeek publish no embeddings endpoint at all.
const EMBEDDING_CAPABLE_PROVIDERS: ReadonlyArray<string> = ['openai', 'google']

// The embedding model each provider is called with. Deliberately NOT the
// config's own model: that is a chat model (gpt-4o, gemini-2.5-pro) and would
// 404 against the embeddings endpoint. The configuration supplies the provider
// and its API key; the model below is what actually runs.
const EMBEDDING_MODELS: Record<string, { model: string; dimensions: number }> = {
  openai: { model: 'text-embedding-3-small', dimensions: 1536 },
  // text-embedding-004 was retired by Google on 2026-01-14 and now 404s.
  google: { model: 'gemini-embedding-001', dimensions: 768 },
}

function EmbeddingSection({
  value,
  configs,
  providerKeySet,
  onChange,
}: {
  value: EmbeddingConfig
  configs: LlmConfig[]
  /** Which embedding-capable providers already have a stored key (masked). */
  providerKeySet: Partial<Record<'openai' | 'google', boolean>>
  onChange: (v: EmbeddingConfig) => void
}) {
  const eligible = configs.filter(
    (c): c is Extract<LlmConfig, { mode: 'api' }> =>
      c.mode === 'api' && EMBEDDING_CAPABLE_PROVIDERS.includes(c.provider),
  )

  // Legacy rows carry no discriminator: a stored provider means the dedicated
  // shape, a stored name means the reuse shape.
  const source: '' | 'config' | 'custom' =
    value.source || (value.config_name ? 'config' : value.provider ? 'custom' : '')

  const selected = eligible.find((c) => c.name === value.config_name)
  const reuseProvider = selected?.provider as 'openai' | 'google' | undefined
  const activeProvider = source === 'custom' ? value.provider : reuseProvider
  const embedding = activeProvider ? EMBEDDING_MODELS[activeProvider] : undefined
  const model = value.model || embedding?.model || ''
  const dimensions = value.dimensions || (value.model ? 0 : embedding?.dimensions || 0)
  const hasKey = activeProvider ? !!providerKeySet[activeProvider] : false
  // A self-hosted endpoint normally wants no credential, so it stands in for
  // the vendor key rather than being required alongside it.
  const credentialOk = hasKey || !!(value.base_url || '').trim()

  function setMode(next: '' | 'config' | 'custom') {
    if (next === '') { onChange({}); return }
    if (next === 'config') {
      onChange({ source: 'config', config_name: eligible[0]?.name || '' })
      return
    }
    onChange({ source: 'custom', provider: value.provider || 'openai' })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 620 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, alignItems: 'center' }}>
        <label style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Embeddings from</label>
        <select
          className="input"
          value={source}
          onChange={(e) => setMode(e.target.value as '' | 'config' | 'custom')}
        >
          <option value="">Disabled (no vector search)</option>
          <option value="config" disabled={eligible.length === 0}>
            {eligible.length === 0
              ? 'An LLM configuration (none is API mode on OpenAI/Google)'
              : 'An LLM configuration above (reuse its provider and key)'}
          </option>
          <option value="custom">A dedicated embedding provider</option>
        </select>
      </div>

      {source === 'config' && (
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, alignItems: 'center' }}>
          <label style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Configuration</label>
          <select
            className="input"
            value={value.config_name || ''}
            onChange={(e) => onChange({ source: 'config', config_name: e.target.value })}
          >
            <option value="">Select a configuration…</option>
            {eligible.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name} — {c.provider === 'openai' ? 'OpenAI' : 'Google'}
              </option>
            ))}
          </select>
        </div>
      )}

      {source === 'custom' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Provider</label>
            <select
              className="input"
              value={value.provider || 'openai'}
              onChange={(e) =>
                // The model pin belongs to the provider that was chosen with
                // it — carrying it across would send a Google model name to
                // OpenAI's endpoint.
                onChange({
                  ...value,
                  source: 'custom',
                  provider: e.target.value as 'openai' | 'google',
                  model: undefined,
                  dimensions: undefined,
                })
              }
            >
              <option value="openai">OpenAI</option>
              <option value="google">Google</option>
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Model</label>
            <input
              className="input"
              value={value.model || ''}
              placeholder={embedding?.model || ''}
              onChange={(e) => onChange({ ...value, source: 'custom', model: e.target.value })}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Endpoint</label>
            <input
              className="input"
              value={value.base_url || ''}
              placeholder="Optional — a self-hosted, OpenAI-compatible embeddings URL"
              onChange={(e) => onChange({ ...value, source: 'custom', base_url: e.target.value })}
            />
          </div>
        </>
      )}

      {source !== '' && (
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.6 }}>
          {model ? (
            <>
              Master records are embedded with <strong>{model}</strong>
              {dimensions ? ` (${dimensions} dimensions)` : ''}
              {activeProvider ? ` via ${activeProvider === 'openai' ? 'OpenAI' : 'Google'}` : ''}.
              Changing this later means re-indexing every master, so the stored vectors match what
              searches are compared against.
            </>
          ) : (
            <>Pick a provider to see the embedding model and vector width.</>
          )}
        </p>
      )}

      {source === 'custom' && activeProvider && !credentialOk && (
        <p style={{ fontSize: 13, color: 'var(--color-warning, #d97706)', margin: 0, lineHeight: 1.6 }}>
          No {activeProvider === 'openai' ? 'OpenAI' : 'Google'} API key is stored yet. Enter it
          under <strong>Provider API keys</strong> above — the field appears there as soon as this
          provider is selected. Without a key (or a self-hosted endpoint) records are indexed for
          text matching only.
        </p>
      )}

      {source === 'config' && selected && !providerKeySet[selected.provider as 'openai' | 'google'] && (
        <p style={{ fontSize: 13, color: 'var(--color-warning, #d97706)', margin: 0, lineHeight: 1.6 }}>
          <strong>{selected.name}</strong> has no stored API key. Add it under{' '}
          <strong>Provider API keys</strong> above, or switch to a dedicated embedding provider.
        </p>
      )}

      {source === '' && eligible.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.6 }}>
          None of your LLM configurations can embed — an <strong>Agent</strong> configuration
          authenticates a CLI against a subscription and exposes no provider key, and only OpenAI
          and Google publish an embeddings endpoint. Choose{' '}
          <strong>A dedicated embedding provider</strong> above to configure one just for vector
          search.
        </p>
      )}
    </div>
  )
}

function ImageGenSection({
  value,
  configs,
  onChange,
}: {
  value: ImageGenConfig
  configs: LlmConfig[]
  onChange: (v: ImageGenConfig) => void
}) {
  // Only API-mode configs on an image-capable provider can generate images.
  const eligible = configs.filter(
    (c): c is Extract<LlmConfig, { mode: 'api' }> =>
      c.mode === 'api' && IMAGE_CAPABLE_PROVIDERS.includes(c.provider),
  )
  const selected = eligible.find((c) => c.name === value.config_name)

  // A legacy profile (direct provider/model, no config_name) is still in
  // effect — surface it so the user knows why generation works, and nudge
  // them to pick a config instead.
  const hasLegacy = !value.config_name && !!value.provider

  if (eligible.length === 0) {
    return (
      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: 0, maxWidth: 560 }}>
        No image-capable configurations yet. Add an <strong>API</strong> configuration above using
        the <strong>OpenAI</strong> or <strong>Google</strong> provider, then select it here.
      </p>
    )
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, alignItems: 'center', maxWidth: 560 }}>
      <label style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Configuration</label>
      <select
        className="input"
        value={value.config_name || ''}
        onChange={(e) => {
          const name = e.target.value
          // Selecting a config drops any legacy provider/model so the named
          // config is the single source of truth.
          onChange(name ? { config_name: name } : {})
        }}
      >
        <option value="">Disabled (no AI image generation)</option>
        {eligible.map((c) => (
          <option key={c.name} value={c.name}>
            {c.name} — {c.provider === 'openai' ? 'OpenAI' : 'Google'} · {c.model}
          </option>
        ))}
      </select>

      {selected && (
        <>
          <span />
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Images use <strong>{selected.model}</strong> via your{' '}
            {selected.provider === 'openai' ? 'OpenAI' : 'Google'} API key.
          </span>
        </>
      )}

      {hasLegacy && (
        <>
          <span />
          <span style={{ fontSize: 13, color: 'var(--color-warning, #b45309)' }}>
            Currently using a legacy {value.provider} / {value.model || 'default'} profile. Pick a
            configuration above to switch to a reusable one.
          </span>
        </>
      )}
    </div>
  )
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <section>
      <h3 style={{ margin: '0 0 4px 0', fontSize: 16, fontWeight: 600 }}>{title}</h3>
      {subtitle && (
        <p
          style={{
            margin: '0 0 12px 0',
            fontSize: 14,
            color: 'var(--color-text-secondary)',
          }}
        >
          {subtitle}
        </p>
      )}
      {children}
    </section>
  )
}
